import React, { useState, useEffect } from "react";
import Login from "./components/auth/Login";
import Header from "./components/layout/Header";
import Sidebar from "./components/layout/Sidebar";
import AdminDashboard from "./components/dashboard/AdminDashboard";
import MemberDashboard from "./components/dashboard/MemberDashboard";
import PoliciesManager from "./components/policies/PoliciesManager";
import MembersManager from "./components/members/MembersManager";
import ClaimsManager from "./components/claims/ClaimsManager";
import PaymentsManager from "./components/payments/PaymentsManager";
import FraudManager from "./components/fraud/FraudManager";
import SettingsPage from "./components/settings/SettingsPage";
import apiService from "./services/apiService";

const App = () => {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);

  // Login handler
  const handleLogin = async (authData) => {
    try {
      if (authData?.token) {
        apiService.setAuthToken(authData.token);
      }
      const me = await apiService.getMe();
      const realUser = me.user ?? me;
      setUser(realUser);
      apiService.setUser(realUser);
    } catch (err) {
      console.error("Error after login fetching user:", err);
      if (authData?.user) {
        setUser(authData.user);
        apiService.setUser(authData.user);
      }
    }
  };

  // Logout handler
  const handleLogout = () => {
    setUser(null);
    setActiveTab("dashboard");
    apiService.removeAuthToken();
    apiService.removeUser();
    localStorage.removeItem("auth_token");
  };

  // On mount → check token & fetch user
  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setLoading(false);
        return;
      }

      apiService.setAuthToken(token);
      try {
        const me = await apiService.getMe();
        const realUser = me.user ?? me;
        setUser(realUser);
        apiService.setUser(realUser);
      } catch (err) {
        console.error("Failed to validate token:", err);
        apiService.removeAuthToken();
        apiService.removeUser();
        localStorage.removeItem("auth_token");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":
        return user.role === "admin" ? <AdminDashboard /> : <MemberDashboard />;
      case "members":
        return user.role === "admin" ? <MembersManager /> : null;
      case "policies":
        return <PoliciesManager userRole={user.role} />;
      case "claims":
        return <ClaimsManager userRole={user.role} />;
      case "payments":
        return user.role === "admin" ? <PaymentsManager /> : null;
      case "fraud":
        return user.role === "admin" ? <FraudManager /> : null;
      case "settings":
        return <SettingsPage user={user} />;
      default:
        return user.role === "admin" ? <AdminDashboard /> : <MemberDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} onLogout={handleLogout} />
      <div className="flex h-[calc(100vh-80px)]">
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          userRole={user.role}
        />
        <main className="flex-1 overflow-y-auto">{renderContent()}</main>
      </div>
    </div>
  );
};

export default App;
